/**
 * Event Pricing tab for Profile Event Essentials.
 * Regular pricing (one price per event type) and special pricing (Holi, Christmas, New Year).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, IndianRupee, Sparkles } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Select, SelectOption } from '../../../components/ui/Select';
import { MultiSelect } from '../../../components/ui/MultiSelect';
import { Button } from '../../../components/ui/Button';
import { Sheet } from '../../../components/ui/Sheet';
import type { EventPricingApiItem } from '../../../services/artistService';
import { useEventPricingStore } from '../../../stores/eventPricingStore';
import { EVENT_TYPES } from '../../../constants/eventTypes';
import { cn } from '../../../utils/cn';
import { Loader } from '@/components/ui';
import { AlertCircle } from 'lucide-react';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { ProfileIncompleteCornerPing } from '@/components/profile/ProfileCompletionBlocksControl';
import { toEventTypeDisplayLabel } from '@/utils/eventTypeDisplay';

function mapEventPricingFromApi(items: EventPricingApiItem[]): EventPricing[] {
    return items.map((item) => {
        const fp = item.festival_pricing || {};
        return {
            id: String(item.event.id),
            eventName: toEventTypeDisplayLabel(item.event.event_name),
            eventTypeSlug: item.event.e_slug,
            price: parseFloat(item.base_price) || 0,
            christmas: fp.christmas ? parseFloat(fp.christmas) : undefined,
            holi: fp.holi ? parseFloat(fp.holi) : undefined,
            newYear: fp.new_year ? parseFloat(fp.new_year) : undefined,
        };
    });
}

type Occasion = 'christmas' | 'holi' | 'newYear';

export interface EventPricing {
    id: string;
    eventName: string;
    eventTypeSlug?: string;
    price: number;
    christmas?: number;
    holi?: number;
    newYear?: number;
}

export const EventPricingTab: React.FC = () => {
    const [events, setEvents] = useState<EventPricing[]>([]);
    const eventPricingData = useEventPricingStore((s) => s.data);
    const eventPricingLoading = useEventPricingStore((s) => s.isLoading);
    const ensureEventPricingFetched = useEventPricingStore((s) => s.ensureFetched);
    const storeCreateEventPricing = useEventPricingStore((s) => s.createEventPricing);
    const storeUpdateEventPricing = useEventPricingStore((s) => s.updateEventPricing);
    const storeDeleteEventPricing = useEventPricingStore((s) => s.deleteEventPricing);
    const storePatchSpecialEventPricing = useEventPricingStore((s) => s.patchSpecialEventPricing);
    const storeDeleteSpecialEventPricing = useEventPricingStore((s) => s.deleteSpecialEventPricing);

    const incompletedSections = useArtistDetailsStore((s) => s.data?.incompleted_sections);
    const ensureArtistDetailsFetched = useArtistDetailsStore((s) => s.ensureFetched);

    const eventPricingNeedAttention = useMemo(() => {
        if (!Array.isArray(incompletedSections)) return false;
        return incompletedSections.includes('event_pricing');
    }, [incompletedSections]);

    const [deletePricingId, setDeletePricingId] = useState<string | null>(null);
    const [deletePricingLoading, setDeletePricingLoading] = useState(false);
    const [clearSpecialPricingId, setClearSpecialPricingId] = useState<string | null>(null);

    const [pricingSubView, setPricingSubView] = useState<'regular' | 'special'>('regular');
    const [isEventSheetOpen, setIsEventSheetOpen] = useState(false);
    const [saveEventLoading, setSaveEventLoading] = useState(false);
    const [editingEventId, setEditingEventId] = useState<string | null>(null);
    const [eventFormData, setEventFormData] = useState({
        eventTypeSlug: '',
        eventName: '',
        price: '',
        christmas: '',
        holi: '',
        newYear: '',
        applyPriceToAllEvents: false,
    });
    const [isSpecialSheetOpen, setIsSpecialSheetOpen] = useState(false);
    const [editingSpecialEventId, setEditingSpecialEventId] = useState<string | null>(null);
    const [addSpecialFormData, setAddSpecialFormData] = useState({ eventIds: [] as string[], occasions: [] as Occasion[], price: '' });
    const [editSpecialFormData, setEditSpecialFormData] = useState({ christmas: '', holi: '', newYear: '' });
    const [saveSpecialPricingLoading, setSaveSpecialPricingLoading] = useState(false);
    const [clearSpecialPricingLoading, setClearSpecialPricingLoading] = useState(false);

    useEffect(() => {
        ensureEventPricingFetched();
    }, [ensureEventPricingFetched]);

    useEffect(() => {
        void ensureArtistDetailsFetched();
    }, [ensureArtistDetailsFetched]);

    useEffect(() => {
        setEvents(mapEventPricingFromApi(eventPricingData ?? []));
    }, [eventPricingData]);

    /** Comma only for 5-digit amounts in UI: 5000 → 5000, 50000 → 50,000. Payload stays raw. */
    const formatIndianRupee = (raw: string): string => {
        const digits = raw.replace(/\D/g, '');
        if (digits === '') return '';
        if (digits.length < 5) return digits;
        return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const addedSlugs = new Set(events.map((e) => e.eventTypeSlug).filter(Boolean));
    const addedNames = new Set(events.map((e) => e.eventName?.trim()).filter(Boolean));
    const availableEventTypeOptions: SelectOption[] = EVENT_TYPES.filter(
        (t) => !addedSlugs.has(t.e_slug) && !addedNames.has(toEventTypeDisplayLabel(t.event_name))
    ).map((t) => ({ value: t.e_slug, label: toEventTypeDisplayLabel(t.event_name) }));

    const openEventSheet = (event?: EventPricing) => {
        if (event) {
            setEditingEventId(event.id);
            setEventFormData({
                eventTypeSlug: event.eventTypeSlug ?? '',
                eventName: event.eventName,
                price: String(event.price),
                christmas: '',
                holi: '',
                newYear: '',
                applyPriceToAllEvents: false,
            });
        } else {
            setEditingEventId(null);
            setEventFormData({ eventTypeSlug: '', eventName: '', price: '', christmas: '', holi: '', newYear: '', applyPriceToAllEvents: false });
        }
        setIsEventSheetOpen(true);
    };

    const closeEventSheet = () => {
        setIsEventSheetOpen(false);
        setEditingEventId(null);
        setEventFormData({ eventTypeSlug: '', eventName: '', price: '', christmas: '', holi: '', newYear: '', applyPriceToAllEvents: false });
    };

    const handleSaveEvent = async () => {
        const price = parseFloat(eventFormData.price);
        if (isNaN(price) || price < 0) return;

        if (editingEventId) {
            const slug = eventFormData.eventTypeSlug?.trim();
            if (!slug) return;
            const basePrice = price.toFixed(2);
            setSaveEventLoading(true);
            try {
                await storeUpdateEventPricing(slug, {
                    base_price: basePrice,
                    apply_to_all: eventFormData.applyPriceToAllEvents,
                    festival_pricing: {},
                });
                closeEventSheet();
            } catch {
                // Keep sheet open
            } finally {
                setSaveEventLoading(false);
            }
            return;
        }

        const slug = eventFormData.eventTypeSlug.trim();
        const basePrice = price.toFixed(2);
        setSaveEventLoading(true);
        try {
            await storeCreateEventPricing({
                event_slug: slug,
                base_price: basePrice,
                festival_pricing: {},
            });
            closeEventSheet();
        } catch {
            // Keep sheet open
        } finally {
            setSaveEventLoading(false);
        }
    };

    const deleteEventPricing = async (id: string) => {
        const event = events.find((e) => e.id === id);
        const slug = event?.eventTypeSlug;
        if (!slug) {
            setEvents((prev) => prev.filter((e) => e.id !== id));
            setDeletePricingId(null);
            if (editingEventId === id) closeEventSheet();
            return;
        }
        setDeletePricingLoading(true);
        try {
            await storeDeleteEventPricing(slug);
            setDeletePricingId(null);
            if (editingEventId === id) closeEventSheet();
        } catch {
            // Keep modal open
        } finally {
            setDeletePricingLoading(false);
        }
    };

    const clearSpecialPricingForEvent = async (id: string) => {
        const event = events.find((e) => e.id === id);
        const slug = event?.eventTypeSlug;
        if (!slug) {
            setClearSpecialPricingId(null);
            return;
        }
        setClearSpecialPricingLoading(true);
        try {
            await storeDeleteSpecialEventPricing(slug);
            setClearSpecialPricingId(null);
        } catch {
            // Keep modal open; store sets error
        } finally {
            setClearSpecialPricingLoading(false);
        }
    };

    const openAddSpecialSheet = () => {
        setEditingSpecialEventId(null);
        setAddSpecialFormData({ eventIds: [], occasions: [], price: '' });
        setIsSpecialSheetOpen(true);
    };

    const openEditSpecialSheet = (event: EventPricing) => {
        setEditingSpecialEventId(event.id);
        setEditSpecialFormData({
            christmas: event.christmas != null ? String(event.christmas) : '',
            holi: event.holi != null ? String(event.holi) : '',
            newYear: event.newYear != null ? String(event.newYear) : '',
        });
        setIsSpecialSheetOpen(true);
    };

    const closeSpecialSheet = () => {
        setIsSpecialSheetOpen(false);
        setEditingSpecialEventId(null);
        setAddSpecialFormData({ eventIds: [], occasions: [], price: '' });
        setEditSpecialFormData({ christmas: '', holi: '', newYear: '' });
    };

    const handleSaveSpecialPricing = async () => {
        if (editingSpecialEventId) {
            const event = events.find((e) => e.id === editingSpecialEventId);
            const slug = event?.eventTypeSlug;
            if (!slug) return;
            const c = editSpecialFormData.christmas ? parseFloat(editSpecialFormData.christmas) : undefined;
            const h = editSpecialFormData.holi ? parseFloat(editSpecialFormData.holi) : undefined;
            const n = editSpecialFormData.newYear ? parseFloat(editSpecialFormData.newYear) : undefined;
            if (c !== undefined && (isNaN(c) || c < 0)) return;
            if (h !== undefined && (isNaN(h) || h < 0)) return;
            if (n !== undefined && (isNaN(n) || n < 0)) return;
            const festival_pricing: Record<string, string> = {};
            if (editSpecialFormData.christmas.trim() !== '') festival_pricing.christmas = editSpecialFormData.christmas.trim();
            if (editSpecialFormData.holi.trim() !== '') festival_pricing.holi = editSpecialFormData.holi.trim();
            if (editSpecialFormData.newYear.trim() !== '') festival_pricing.new_year = editSpecialFormData.newYear.trim();
            setSaveSpecialPricingLoading(true);
            try {
                await storePatchSpecialEventPricing(slug, { festival_pricing });
                closeSpecialSheet();
            } catch {
                // Keep sheet open; store sets error
            } finally {
                setSaveSpecialPricingLoading(false);
            }
            return;
        }

        const price = parseFloat(addSpecialFormData.price);
        if (isNaN(price) || price < 0) return;
        const { eventIds, occasions } = addSpecialFormData;
        if (!eventIds?.length || occasions.length === 0) return;

        const priceStr = price.toFixed(2);
        const festival_pricing: Record<string, string> = {};
        if (occasions.includes('christmas')) festival_pricing.christmas = priceStr;
        if (occasions.includes('holi')) festival_pricing.holi = priceStr;
        if (occasions.includes('newYear')) festival_pricing.new_year = priceStr;

        const selectedEvents = eventIds.map((id) => events.find((e) => e.id === id)).filter((e): e is EventPricing => !!e && !!e.eventTypeSlug);
        if (selectedEvents.length === 0) return;

        setSaveSpecialPricingLoading(true);
        try {
            await Promise.all(
                selectedEvents.map((event) =>
                    storeCreateEventPricing({
                        event_slug: event.eventTypeSlug!,
                        base_price: event.price.toFixed(2),
                        festival_pricing,
                    })
                )
            );
            closeSpecialSheet();
        } catch {
            // Keep sheet open
        } finally {
            setSaveSpecialPricingLoading(false);
        }
    };

    const eventsWithSpecialPricing = events.filter((e) => e.christmas != null || e.holi != null || e.newYear != null);

    const occasionOptions: SelectOption[] = [
        { value: 'christmas', label: 'Christmas' },
        { value: 'holi', label: 'Holi' },
        { value: 'newYear', label: 'New Year' },
    ];

    return (
        <>
            <motion.div
                key="pricing"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
            >
                <div className="rounded-2xl -mb-5">
                    <div className="inline-flex rounded-full p-1 text-xs gap-2 px-4">
                        {[
                            { id: 'regular' as const, label: 'Regular Pricing' },
                            { id: 'special' as const, label: 'Special Pricing' },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setPricingSubView(tab.id)}
                                className={cn(
                                    'px-3 py-1.5 rounded-full transition-colors whitespace-nowrap',
                                    pricingSubView === tab.id ? 'bg-[#F33363] text-white' : 'text-[#F2F2F7] hover:bg-[#2C2C2E]'
                                )}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {pricingSubView === 'regular' && (
                    <motion.section
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className=" rounded-2xl border border-[#3A3A3C] p-5 lg:p-6 space-y-5"
                    >
                        {eventPricingLoading && (
                            <div className="flex items-center justify-center py-12 text-[#8E8E93] text-sm">
                                <Loader size="small" />
                            </div>
                        )}
                        {!eventPricingLoading && (
                            <>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div>
                                        <h2 className="text-base lg:text-lg font-semibold text-white flex items-center gap-2">
                                            <IndianRupee className="h-5 w-5 text-[#F33363]" />
                                            Regular Pricing
                                        </h2>
                                        <p className="text-sm text-[#8E8E93] mt-1 max-w-md">
                                            Your standard pricing per event type. One price per event (e.g. Wedding, Birthday). Add events, set base price, edit or apply one price to all.
                                        </p>
                                    </div>
                                    <span className="relative inline-flex w-fit shrink-0 self-start sm:self-center">
                                        <button
                                            type="button"
                                            onClick={() => openEventSheet()}
                                            disabled={availableEventTypeOptions.length === 0}
                                            className="relative z-0 inline-flex w-fit items-center gap-1.5 rounded-full border border-[#3A3A3C] bg-[#151517] px-3 py-1.5 text-[11px] text-[#F2F2F7] text-nowrap transition-colors hover:bg-[#2C2C2E] disabled:pointer-events-none disabled:opacity-50"
                                        >
                                            <Plus className="h-3.5 w-3.5" />
                                            Add event
                                        </button>
                                        {eventPricingNeedAttention ? (
                                            <ProfileIncompleteCornerPing className="right-0 top-0 z-[2] md:right-0 md:top-0" />
                                        ) : null}
                                    </span>
                                </div>

                                {events.length === 0 ? (
                                    <div className="py-16 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#3A3A3C] bg-[#0B0B0D]/30">
                                        <div className="w-14 h-14 rounded-full bg-[#2C2C2E] flex items-center justify-center mb-4">
                                            <IndianRupee className="h-7 w-7 text-[#5A5A5E]" />
                                        </div>
                                        <p className="text-white font-semibold text-base">No events yet</p>
                                        <p className="text-sm text-[#8E8E93] mt-1 text-center px-4">Add your first event type and set its regular price.</p>
                                        {/* <button
                                            type="button"
                                            onClick={() => openEventSheet()}
                                            disabled={availableEventTypeOptions.length === 0}
                                            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap disabled:opacity-50 disabled:pointer-events-none"
                                        >
                                            <Plus className="h-3.5 w-3.5" />
                                            Add first event
                                        </button> */}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider px-1">Your events ({events.length})</p>
                                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                                            {events.map((event) => (
                                                <motion.div
                                                    key={event.id}
                                                    layout
                                                    className="flex justify-between items-center gap-3 p-3 sm:p-4 rounded-xl bg-[#151517] border border-[#3A3A3C] hover:bg-[#1a1a1c] transition-colors"
                                                >
                                                    <button type="button" onClick={() => openEventSheet(event)} className="flex flex-col min-w-0 text-left">
                                                        <span className="text-sm font-semibold text-white block truncate">{event.eventName}</span>
                                                        <span className="text-xs text-[#8E8E93]">₹{event.price.toLocaleString()}</span>
                                                    </button>
                                                    <div className="flex items-center gap-1 shrink-0">
                                                        <button onClick={() => openEventSheet(event)} className="h-8 w-8 flex items-center justify-center rounded-xl text-[#8E8E93] hover:text-white hover:bg-white/10 border border-[#3A3A3C]" title="Edit">
                                                            <Edit2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </motion.section>
                )}

                {pricingSubView === 'special' && (
                    <motion.section
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className=" rounded-2xl border border-[#3A3A3C] p-5 lg:p-6 space-y-5"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <h2 className="text-base lg:text-lg font-semibold text-white flex items-center gap-2">
                                    <Sparkles className="h-5 w-5 text-[#F33363]" />
                                    Special Pricing
                                </h2>
                                <p className="text-sm text-[#8E8E93] mt-1 max-w-md">
                                    Peak-season pricing for occasions (Holi, Christmas, New Year). Shown in addition to your regular price per event type.
                                </p>
                            </div>
                            {events.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => openAddSpecialSheet()}
                                    className="inline-flex items-center gap-1.5 rounded-full w-fit bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    Add special pricing
                                </button>
                            )}
                        </div>

                        {events.length === 0 ? (
                            <div className="py-12 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#3A3A3C] bg-[#0B0B0D]/30">
                                <p className="text-white font-semibold text-sm">Add regular events first</p>
                                <p className="text-sm text-[#8E8E93] mt-1 text-center px-4">Add event types in Regular pricing, then come back here to set occasion-based pricing.</p>
                                <Button onClick={() => setPricingSubView('regular')} variant="active" className="mt-4 py-2.5 px-5 rounded-xl">
                                    Go to Regular pricing
                                </Button>
                            </div>
                        ) : eventsWithSpecialPricing.length === 0 ? (
                            <div className="py-12 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#3A3A3C] bg-[#0B0B0D]/30">
                                <p className="text-white font-semibold text-sm">No special pricing yet</p>
                                <p className="text-sm text-[#8E8E93] mt-1 text-center px-4">Add occasion prices for Holi, Christmas or New Year per event type.</p>

                            </div>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider px-1">Your special events ({eventsWithSpecialPricing.length})</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {eventsWithSpecialPricing.map((event) => {
                                        const rows = [
                                            event.christmas != null && { label: 'Christmas', value: event.christmas },
                                            event.holi != null && { label: 'Holi', value: event.holi },
                                            event.newYear != null && { label: 'New Year', value: event.newYear },
                                        ].filter(Boolean) as { label: string; value: number }[];
                                        return (
                                            <motion.div
                                                key={event.id}
                                                layout
                                                className="rounded-xl bg-[#151517] border border-[#3A3A3C] p-4 hover:bg-[#1a1a1c] transition-colors"
                                            >
                                                <div className="flex items-start justify-between gap-2 mb-3">
                                                    <span className="text-sm font-semibold text-white">{event.eventName}</span>
                                                    <button onClick={() => openEditSpecialSheet(event)} className="h-9 w-9 flex items-center justify-center rounded-lg border border-[#3A3A3C] text-[#8E8E93] hover:text-white hover:bg-white/10 shrink-0" title="Edit occasion prices">
                                                        <Edit2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                                <div className="space-y-1.5">
                                                    {rows.map((r) => (
                                                        <div key={r.label} className="flex justify-between text-xs">
                                                            <span className="text-[#8E8E93]">{r.label}</span>
                                                            <span className="text-white font-medium">₹{r.value.toLocaleString()}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </motion.section>
                )}
            </motion.div>

            <Sheet open={isEventSheetOpen} onOpenChange={setIsEventSheetOpen} title={editingEventId ? 'Edit event' : 'Add event'} description={editingEventId ? 'Update event name and regular price.' : 'Add a new event type with its regular price.'}>
                <div className="space-y-6 pt-4">
                    {editingEventId ? (
                        <div>
                            <label className="mb-2 block text-sm font-medium text-[#8E8E93]">Event type</label>
                            <p className="text-sm font-semibold text-white">{eventFormData.eventName}</p>
                        </div>
                    ) : (
                        <Select label="Event type" value={eventFormData.eventTypeSlug} onChange={(v) => setEventFormData({ ...eventFormData, eventTypeSlug: v })} options={availableEventTypeOptions} placeholder="Select event type" />
                    )}
                    <Input label="Regular price (₹)" value={formatIndianRupee(eventFormData.price)} onChange={(e) => setEventFormData({ ...eventFormData, price: e.target.value.replace(/\D/g, '') })} placeholder="Standard pricing" inputMode="numeric" />
                    {editingEventId && (
                        <Checkbox checked={eventFormData.applyPriceToAllEvents} onCheckedChange={(c) => setEventFormData({ ...eventFormData, applyPriceToAllEvents: !!c })} label="Apply this price to all event types" className="min-h-[40px]" />
                    )}
                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="active"
                            className="flex-1 py-3 rounded-xl font-semibold"
                            onClick={handleSaveEvent}
                            disabled={editingEventId ? !eventFormData.eventName.trim() || !eventFormData.price : !eventFormData.eventTypeSlug.trim() || !eventFormData.price}
                            isLoading={saveEventLoading && !editingEventId}
                        >
                            {editingEventId ? 'Save changes' : 'Add event'}
                        </Button>
                        {editingEventId && (
                            <button
                                onClick={() => {
                                    setDeletePricingId(editingEventId);
                                    closeEventSheet();
                                }}
                                className="h-12 px-5 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500/20 font-semibold transition-colors shrink-0"
                                title="Remove event"
                            >
                                Delete
                            </button>
                        )}
                    </div>
                </div>
            </Sheet>

            <Sheet open={isSpecialSheetOpen} onOpenChange={(open) => !open && closeSpecialSheet()} title={editingSpecialEventId ? 'Edit Occasion Prices' : 'Update Special Pricing'} description={editingSpecialEventId ? 'Set or change Holi, Christmas and New Year pricing for this event type.' : 'Pick an event type, occasion(s), and one price to add.'}>
                <div className="space-y-6 pt-4">
                    {editingSpecialEventId ? (
                        <>
                            <p className="text-sm text-[#8E8E93]">
                                Event: <span className="font-semibold text-white">{events.find((e) => e.id === editingSpecialEventId)?.eventName}</span>
                            </p>
                            <div className="space-y-3">
                                <Input label="Christmas (₹)" value={formatIndianRupee(editSpecialFormData.christmas)} onChange={(e) => setEditSpecialFormData({ ...editSpecialFormData, christmas: e.target.value.replace(/\D/g, '') })} placeholder="Optional" inputMode="numeric" />
                                <Input label="Holi (₹)" value={formatIndianRupee(editSpecialFormData.holi)} onChange={(e) => setEditSpecialFormData({ ...editSpecialFormData, holi: e.target.value.replace(/\D/g, '') })} placeholder="Optional" inputMode="numeric" />
                                <Input label="New Year (₹)" value={formatIndianRupee(editSpecialFormData.newYear)} onChange={(e) => setEditSpecialFormData({ ...editSpecialFormData, newYear: e.target.value.replace(/\D/g, '') })} placeholder="Optional" inputMode="numeric" />
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <div className="mb-2 flex items-center justify-between">
                                    <label className="block text-sm font-medium text-white">Event types</label>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setAddSpecialFormData((prev) => ({
                                                ...prev,
                                                eventIds: events.length && prev.eventIds.length < events.length ? events.map((e) => e.id) : [],
                                            }))
                                        }
                                        className="text-xs font-medium text-[#F33363] hover:text-[#ff5c8d] transition-colors"
                                    >
                                        {addSpecialFormData.eventIds.length >= events.length && events.length > 0 ? 'Deselect all' : 'Select all'}
                                    </button>
                                </div>
                                <p className="text-xs text-[#8E8E93] mb-2">Select one or more; the same pricing will apply to each.</p>
                                <MultiSelect options={events.map((e) => ({ value: e.id, label: e.eventName }))} value={addSpecialFormData.eventIds} onChange={(v) => setAddSpecialFormData({ ...addSpecialFormData, eventIds: v })} />
                            </div>
                            <div>
                                <div className="mb-2 flex items-center justify-between">
                                    <label className="block text-sm font-medium text-white">Occasions</label>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setAddSpecialFormData((prev) => ({
                                                ...prev,
                                                occasions: prev.occasions.length < 3 ? (['christmas', 'holi', 'newYear'] as Occasion[]) : [],
                                            }))
                                        }
                                        className="text-xs font-medium text-[#F33363] hover:text-[#ff5c8d] transition-colors"
                                    >
                                        {addSpecialFormData.occasions.length >= 3 ? 'Deselect all' : 'Select all'}
                                    </button>
                                </div>
                                <p className="text-xs text-[#8E8E93] mb-2">Select one or more; the same price will apply to each.</p>
                                <MultiSelect options={occasionOptions} value={addSpecialFormData.occasions} onChange={(v) => setAddSpecialFormData({ ...addSpecialFormData, occasions: v as Occasion[] })} />
                            </div>
                            <Input label="Price (₹)" value={formatIndianRupee(addSpecialFormData.price)} onChange={(e) => setAddSpecialFormData({ ...addSpecialFormData, price: e.target.value.replace(/\D/g, '') })} placeholder="Price for selected Occasions and Events" inputMode="numeric" />
                        </>
                    )}
                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="active"
                            className="flex-1 py-3 rounded-xl font-semibold"
                            onClick={handleSaveSpecialPricing}
                            disabled={saveSpecialPricingLoading || (editingSpecialEventId ? false : !addSpecialFormData.eventIds?.length || addSpecialFormData.occasions.length === 0 || !addSpecialFormData.price)}
                        >
                            {editingSpecialEventId ? 'Save changes' : saveSpecialPricingLoading ? <Loader size="small" /> : 'Add special pricing'}
                        </Button>
                        {editingSpecialEventId && (
                            <button
                                onClick={() => {
                                    setClearSpecialPricingId(editingSpecialEventId);
                                    closeSpecialSheet();
                                }}
                                className="h-12 px-5 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500/20 font-semibold transition-colors shrink-0"
                                title="Remove special pricing for this event"
                            >
                                Delete
                            </button>
                        )}
                    </div>
                </div>
            </Sheet>

            <AnimatePresence>
                {deletePricingId && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDeletePricingId(null)} className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="relative w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C]/50"
                        >
                            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                                <AlertCircle className="h-8 w-8 text-red-500" />
                            </div>
                            <h3 className="text-2xl font-black text-center text-white tracking-tight mb-2">Delete Event?</h3>
                            <p className="text-[#8E8E93] text-center text-sm mb-8">Remove this event and its pricing? This cannot be undone.</p>
                            {(() => {
                                const ev = events.find((e) => e.id === deletePricingId);
                                return ev ? (
                                    <div className="bg-[#0B0B0D] rounded-2xl p-4 mb-8 border border-[#3A3A3C]">
                                        <p className="text-xs font-black text-[#5A5A5E] uppercase tracking-widest mb-1">Event</p>
                                        <p className="text-sm font-bold text-white">{ev.eventName}</p>
                                        <p className="text-xs text-[#8E8E93]">₹{ev.price.toLocaleString()}</p>
                                    </div>
                                ) : null;
                            })()}
                            <div className="flex flex-col gap-3">
                                <Button
                                    variant="active"
                                    className="w-full py-4 rounded-2xl font-bold bg-red-500 hover:bg-red-600 border-none"
                                    onClick={() => deletePricingId && deleteEventPricing(deletePricingId)}
                                    disabled={deletePricingLoading}
                                    isLoading={deletePricingLoading}
                                >
                                    Yes, Delete
                                </Button>
                                <button onClick={() => setDeletePricingId(null)} className="w-full py-4 text-[#8E8E93] font-bold hover:text-white transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {clearSpecialPricingId && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setClearSpecialPricingId(null)} className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="relative w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C]/50"
                        >
                            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
                                <AlertCircle className="h-8 w-8 text-amber-500" />
                            </div>
                            <h3 className="text-xl font-black text-center text-white tracking-tight mb-2">Remove special pricing?</h3>
                            <p className="text-[#8E8E93] text-center text-sm mb-6">This will clear Holi, Christmas and New Year pricing for this event only. The event and its regular price will stay.</p>
                            {(() => {
                                const ev = events.find((e) => e.id === clearSpecialPricingId);
                                return ev ? (
                                    <div className="bg-[#0B0B0D] rounded-2xl p-4 mb-6 border border-[#3A3A3C]">
                                        <p className="text-xs font-black text-[#5A5A5E] uppercase tracking-widest mb-1">Event</p>
                                        <p className="text-sm font-bold text-white">{ev.eventName}</p>
                                    </div>
                                ) : null;
                            })()}
                            <div className="flex flex-col gap-3">
                                <Button
                                    variant="active"
                                    className="w-full py-4 rounded-2xl font-bold bg-amber-500 hover:bg-amber-600 border-none"
                                    onClick={() => clearSpecialPricingId && clearSpecialPricingForEvent(clearSpecialPricingId)}
                                    disabled={clearSpecialPricingLoading}
                                    isLoading={clearSpecialPricingLoading}
                                >
                                    Remove special pricing
                                </Button>
                                <button onClick={() => setClearSpecialPricingId(null)} className="w-full py-4 text-[#8E8E93] font-bold hover:text-white transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};
